import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface MonitoringSource {
  source_type: string;
  source_id: string;
  source_name: string;
}

interface Category {
  id: string;
  dbId?: number;
  slug?: string; // The slug is what is used for categorization
  label: string;
  count: number;
  active: boolean;  // Which tab is currently selected in the UI
  isDismissed: boolean;
  color: string;
  description: string;
  isCustom: boolean;
  is_default?: boolean; // Whether this is a global default category (only 9 should be TRUE)
  is_followed?: boolean;
  is_active?: boolean;  // Whether the category is enabled/disabled
  monitoring_sources?: MonitoringSource[];
}

interface CategoriesStore {
  // State
  categories: Category[];
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  
  // Actions
  setCategories: (categories: Category[]) => void;
  updateCategory: (id: string, updates: Partial<Category>) => void;
  addCategory: (category: Category) => void;
  removeCategory: (id: string) => void;
  setActiveCategory: (id: string) => void;
  reorderCategories: (newOrder: string[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  updateCategoryCounts: (counts: Record<string, number>) => void;
  setLastFetchTime: (time: number) => void;
  clearCache: () => void;
  
  // Computed
  getActiveCategory: () => Category | undefined;
  getCategoryById: (id: string) => Category | undefined;
  shouldRefetch: () => boolean;
}

// Create the store with persistence
export const useCategoriesStore = create<CategoriesStore>()(
  persist(
    (set, get) => ({
      // Initial state
      categories: [],
      isLoading: false,
      error: null,
      lastFetchTime: null,
      
      // Actions
      setCategories: (categories) => {
        set((state) => {
          // Don't clear categories if new array is empty and we have existing categories
          if (categories.length === 0 && state.categories.length > 0) {
            console.warn('[CategoriesStore] ⚠️ Attempted to set empty categories array - preserving existing categories');
            return state; // Don't update - preserve existing categories
          }
          
          // Preserve the active state from the current categories
          const currentActive = state.categories.find(cat => cat.active);
          
          // If we have a currently active category, preserve its active state
          if (currentActive) {
            const updatedCategories = categories.map(cat => ({
              ...cat,
              active: cat.id === currentActive.id
            }));
            return {
              categories: updatedCategories,
              lastFetchTime: Date.now(),
              error: null
            };
          }
          
          // Otherwise, use the categories as-is
          return {
            categories,
            lastFetchTime: Date.now(),
            error: null
          };
        });
      },
      
      updateCategory: (id, updates) => set((state) => ({
        categories: state.categories.map((cat) =>
          cat.id === id ? { ...cat, ...updates } : cat
        ),
      })),
      
      addCategory: (category) => set((state) => {
        // Check if category already exists
        const exists = state.categories.some(cat => cat.id === category.id);
        if (exists) {
          return state;
        }
        return {
          categories: [...state.categories, category],
        };
      }),
      
      removeCategory: (id) => set((state) => ({
        categories: state.categories.filter((cat) => cat.id !== id),
      })),
      
      setActiveCategory: (id) => set((state) => ({
        categories: state.categories.map((cat) => ({
          ...cat,
          active: cat.id === id,
        })),
      })),
      
      reorderCategories: (newOrder) => set((state) => {
        const categoryMap = new Map(state.categories.map(cat => [cat.id, cat]));
        const orderedCategories: Category[] = [];
        const seenIds = new Set<string>();
        
        // Add categories in the new order
        newOrder.forEach(id => {
          const cat = categoryMap.get(id);
          if (cat) {
            orderedCategories.push(cat);
            seenIds.add(id);
          }
        });
        
        // Add any categories not in the new order
        state.categories.forEach(cat => {
          if (!seenIds.has(cat.id)) {
            orderedCategories.push(cat);
          }
        });
        
        return { categories: orderedCategories };
      }),
      
      setLoading: (isLoading) => set({ isLoading }),
      
      setError: (error) => set({ error }),
      
      updateCategoryCounts: (counts) => set((state) => {
        console.log('[CategoriesStore] Updating counts:', counts);
        return {
          categories: state.categories.map((cat) => {
            // Try to match by slug first (most reliable), then id
            const newCount = counts[cat.slug] || counts[cat.id] || 0;
            if (newCount !== cat.count) {
              console.log(`[CategoriesStore] Category ${cat.label} count: ${cat.count} -> ${newCount}`);
            }
            return {
              ...cat,
              count: newCount, // Use 0 if not found (don't preserve old count when filters change)
            };
          }),
        };
      }),
      
      setLastFetchTime: (time) => set({ lastFetchTime: time }),
      
      clearCache: () => set({ 
        categories: [],
        lastFetchTime: null,
        error: null,
        isLoading: false,
      }),
      
      // Computed
      getActiveCategory: () => {
        const state = get();
        return state.categories.find((cat) => cat.active);
      },
      
      getCategoryById: (id) => {
        const state = get();
        return state.categories.find((cat) => cat.id === id);
      },
      
      shouldRefetch: () => {
        const state = get();
        if (!state.lastFetchTime) return true;
        
        // Refetch if more than 5 minutes old
        const fiveMinutes = 5 * 60 * 1000;
        return Date.now() - state.lastFetchTime > fiveMinutes;
      },
    }),
    {
      name: 'categories-storage', // unique name for localStorage key
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        // Only persist categories and their order, not loading states
        categories: state.categories,
        lastFetchTime: state.lastFetchTime,
      }),
    }
  )
);

// Helper hook to get categories with automatic refetch check
export const useCategoriesWithRefetch = () => {
  const store = useCategoriesStore();
  const shouldRefetch = store.shouldRefetch();
  
  return {
    ...store,
    shouldRefetch,
  };
};
